// src/components/PortalSubjectNav.js
//
// The row of subject tiles: Science, Social Studies, Math, Other, then Home.
//
// WHY THIS IS SHARED
// ------------------
// The dynamic portal and the 36 classroom pages each drew their own version
// of the same five buttons. The portal had rounded gradient tiles with icons;
// the classroom pages had flat coloured squares with a hard black shadow, and
// put Home *first*. Same navigation, two appearances, two orders — and the
// classroom copy pointed at the old static subject pages rather than the
// portal, which is how "Other" ended up on a page that looks nothing like the
// rest of the app.
//
// One component now. The styling lives in styles/portal-nav.css, which this
// file imports, so there is nowhere for a second look to grow.
//
// HOME GOES LAST
// --------------
// Home leaves the subject set rather than being one of it. Reading
// "Science · Social Studies · Math · Other · Home" is a list of four subjects
// and then a way out; "Home · Science · …" reads as five peers, one of which
// isn't a subject.

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { NAV_SUBJECTS } from "../data/subjects";
import useAccessLevel from "../hooks/useAccessLevel";
import { dashboardPathFor } from "../utils/accountRole";
import { visibleSubjects, trackForSubject } from "../utils/enrollment";
import "../styles/portal-nav.css";

// The CSS colour classes are keyed by these names, not by the subject token —
// "socialstudies" maps to .portal-nav-social.
const NAV_CLASS = {
  science: "portal-nav-science",
  socialstudies: "portal-nav-social",
  math: "portal-nav-math",
  other: "portal-nav-other",
};

export default function PortalSubjectNav({
  state,
  grade,
  view = "student",
  activeSubject = "",
  onSubjectClick = null,
  onHomeClick = null,
}) {
  const navigate = useNavigate();
  const { profile, isAdmin } = useAccessLevel();

  // WHICH BUTTONS EXIST AT ALL
  // --------------------------
  // K-5 is unchanged: four subjects, every account, because one teacher
  // covers all of them and every child in the room is on the same page.
  //
  // From 6th, a student's schedule is the nav. Someone taking World History
  // II sees Social Studies and nothing else — showing Science and Math would
  // be showing two doors onto rooms they aren't in. There is no "10th grade
  // science" for a student who isn't taking a science; there's Chemistry I,
  // and they'd have to be enrolled in it for the page to mean anything.
  //
  // See src/utils/enrollment.js. An account with no schedule yet gets all
  // four rather than an empty row.
  const subjects = useMemo(() => {
    const allowed = visibleSubjects(profile || {});
    return NAV_SUBJECTS.filter((s) => allowed.includes(s.value));
  }, [profile]);

  const goToSubject = (subject) => {
    if (onSubjectClick) {
      onSubjectClick(subject);
      return;
    }

    // Everything routes through the dynamic portal. The classroom pages used
    // to build paths like /educator/va/va-3rd-educator-other, which is a
    // static page with its own layout, its own nav, and no connection to the
    // Firestore-backed content everything else reads.
    if (!state || !grade) {
      navigate(dashboardPathFor(profile || {}, { isAdmin }));
      return;
    }

    // The track, not the grade. A 10th grader clicking Math must land on
    // /portal/va/geometry/math/student — their actual course — not
    // /portal/va/10th/math/student, which addresses a layout that doesn't
    // exist because "10th grade math" isn't a course anyone teaches.
    //
    // Falls back to the grade token for K-5 and for any subject with no
    // course split, so this is a no-op for every elementary account.
    const track =
      trackForSubject(profile || { state, grade }, subject) || grade;

    navigate(`/portal/${String(state).toLowerCase()}/${track}/${subject}/${view}`);
  };

  const goHome = () => {
    if (onHomeClick) {
      onHomeClick();
      return;
    }
    // Was a hand-rolled role.includes() chain on each page, which sent an
    // unpromoted educator to the student dashboard. See utils/accountRole.js.
    navigate(dashboardPathFor(profile || {}, { isAdmin }));
  };

  return (
    <div className="portal-nav-buttons">
      {subjects.map((subject) => {
        const isActive = activeSubject === subject.value;

        return (
          <button
            key={subject.value}
            type="button"
            className={`portal-nav-tile ${NAV_CLASS[subject.value] || ""}${
              isActive ? " portal-nav-active" : ""
            }`}
            onClick={() => goToSubject(subject.value)}
            aria-current={isActive ? "page" : undefined}
          >
            <span className="portal-nav-icon" aria-hidden="true">
              {subject.icon}
            </span>
            <span className="portal-nav-label">{subject.label}</span>
            <span className="portal-nav-underline" aria-hidden="true" />
          </button>
        );
      })}

      {/* "Add Subjects" and "Other" were removed from the nav (Oct 2026). */}

      <button
        type="button"
        className="portal-nav-tile portal-nav-home"
        onClick={goHome}
      >
        <span className="portal-nav-icon" aria-hidden="true">🏠</span>
        <span className="portal-nav-label">Home</span>
      </button>
    </div>
  );
}
